import type { ReactNode } from "react";

import { useTablistKeyboardNavigation } from "./useTablistKeyboardNavigation";

import styles from "./TabSelector.module.css";

/**
 * A single tab descriptor. `id` is the value passed back to `onChange`; the
 * generic parameter on `TabSelectorProps` keeps this type-safe for callers
 * that use a string-literal union (e.g. `"FREE" | "PREMIUM"`).
 */
export type TabSelectorItem<T extends string = string> = {
  id: T;
  label: ReactNode;
  /** Disabled tabs are still rendered, just non-interactive. */
  disabled?: boolean;
  /** Optional per-tab test id suffix; falls back to `id` when omitted. */
  testId?: string;
};

export type TabSelectorProps<T extends string = string> = {
  items: ReadonlyArray<TabSelectorItem<T>>;
  /** Controlled active tab id. */
  value: T;
  onChange: (next: T) => void;
  /** Accessible name for the tablist (announced by screen readers). */
  ariaLabel?: string;
  className?: string;
  /** Root data-testid; per-tab buttons get `${dataTestId}-${item.testId|item.id}`. */
  "data-testid"?: string;
};

/**
 * A horizontal tab strip with a brand-colored underline on the active tab
 * and an equal-width distribution across all items. Used by the Game Pass
 * → Create Season → Challenges step to switch between the Free and Premium
 * tiers (see Figma — "Create Challenges" screen).
 *
 * Controlled-only — the parent owns `value` and updates it via `onChange`.
 * No internal state so the same instance can be re-rendered against a
 * different source of truth (e.g. URL params, wizard parent state).
 *
 * Keyboard support is supplied by `useTablistKeyboardNavigation` (shared
 * with other in-house tablists, e.g. the seasons filter strip). It
 * implements the WAI-ARIA Authoring Practices "tablist" pattern with
 * automatic activation:
 *   - `Tab` moves focus into / out of the tablist (only the active tab
 *     is in the tab sequence; inactive tabs have `tabIndex={-1}`).
 *   - `ArrowLeft` / `ArrowRight` move to the previous / next enabled tab,
 *     wrapping at the ends.
 *   - `Home` / `End` jump to the first / last enabled tab.
 *   - Disabled tabs are skipped over.
 */
function TabSelector<T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  className = "",
  "data-testid": dataTestId,
}: TabSelectorProps<T>) {
  const { tabRefs, handleKeyDown } = useTablistKeyboardNavigation<T>({
    items,
    value,
    onChange,
  });

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      data-testid={dataTestId}
    >
      {items.map((item, index) => {
        const isActive = item.id === value;
        const tabClassName = `${styles.tab}${isActive ? ` ${styles.tabActive}` : ""}`;
        const tabTestId = dataTestId
          ? `${dataTestId}-${item.testId ?? item.id}`
          : undefined;
        return (
          <button
            key={item.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            className={tabClassName}
            onClick={() => {
              if (item.disabled || isActive) return;
              onChange(item.id);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={tabTestId}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export default TabSelector;
